import { useMemo } from 'react'

import { formatHM, formatMoneyShort } from '../lib/format'
import { local } from '../lib/sync'
import type { Task } from '../types'
import { IconChevron } from './icons'
import { useLocal } from './useLocal'

const MONTHS_N = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

interface Bucket {
  key: string
  label: string
  seconds: number
  toPay: number
  inPayout: number
  paid: number
  count: number
}

/**
 * Деньги считаются из тех же задач, что лежат на телефоне.
 *
 * Отдельного запроса нет намеренно: экран должен работать без связи, а суммы
 * тут те же, что в журнале. Ведомости остаются на компьютере — их собирает
 * расчётчик, и с телефона их не редактируют.
 */
export default function Money({ onOpenTask }: { onOpenTask: (id: number) => void }) {
  const { rows: tasks } = useLocal<Task>(local.tasks)

  const totals = useMemo(() => {
    let toPay = 0
    let inPayout = 0
    for (const t of tasks) {
      const sum = Number(t.amount ?? 0)
      if (t.payment_status === 'to_pay') toPay += sum
      if (t.payment_status === 'in_payout') inPayout += sum
    }
    return { toPay, inPayout }
  }, [tasks])

  const months = useMemo(() => {
    const map = new Map<string, Bucket>()
    for (const t of tasks) {
      const key = t.work_date.slice(0, 7)
      let b = map.get(key)
      if (!b) {
        const [y, m] = key.split('-')
        b = {
          key,
          label: `${MONTHS_N[Number(m) - 1]} ${y}`,
          seconds: 0,
          toPay: 0,
          inPayout: 0,
          paid: 0,
          count: 0,
        }
        map.set(key, b)
      }
      const sum = Number(t.amount ?? 0)
      b.seconds += t.tracked_seconds
      b.count += 1
      if (t.payment_status === 'to_pay') b.toPay += sum
      else if (t.payment_status === 'in_payout') b.inPayout += sum
      else if (t.payment_status === 'paid') b.paid += sum
    }
    return [...map.values()].sort((a, b) => (a.key < b.key ? 1 : -1))
  }, [tasks])

  const unpaid = useMemo(
    () =>
      tasks
        .filter((t) => t.payment_status === 'to_pay' && Number(t.amount ?? 0) > 0)
        .sort((a, b) => (a.work_date < b.work_date ? 1 : -1))
        .slice(0, 20),
    [tasks],
  )

  return (
    <>
      <div className="m-head">
        <div className="m-head-row">
          <div className="m-head-name">Деньги</div>
        </div>
      </div>

      <div className="m-money-hero">
        <div className="m-kpi-label">К выплате</div>
        <div className="m-money-value">{formatMoneyShort(totals.toPay)}</div>
        {totals.inPayout > 0 && (
          <div style={{ fontSize: 13, marginTop: 8, color: 'rgba(255,255,255,0.86)' }}>
            в ведомостях ещё {formatMoneyShort(totals.inPayout)}
          </div>
        )}
      </div>

      <div className="m-block" style={{ marginTop: 18 }}>
        <div className="m-block-title">По месяцам</div>
        <div className="m-list" style={{ margin: 0 }}>
          {months.length === 0 && <div className="m-card m-sub">Пока ничего не учтено</div>}
          {months.map((m) => (
            <div key={m.key} className="m-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{m.label}</span>
                <span className="m-sub">{formatHM(m.seconds)}</span>
              </div>
              <div style={{ display: 'flex', gap: 7, marginTop: 8, flexWrap: 'wrap' }}>
                {m.toPay > 0 && <span className="m-chip m-chip-est">к выплате {formatMoneyShort(m.toPay)}</span>}
                {m.inPayout > 0 && <span className="m-chip m-chip-payout">в ведомости {formatMoneyShort(m.inPayout)}</span>}
                {m.paid > 0 && <span className="m-chip m-chip-done">выплачено {formatMoneyShort(m.paid)}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {unpaid.length > 0 && (
        <div className="m-block">
          <div className="m-block-title">Ждут выплаты</div>
          <div className="m-list" style={{ margin: 0 }}>
            {unpaid.map((t) => (
              <div key={t.id} className="m-card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="m-rowbtn" onClick={() => onOpenTask(t.id)}>
                  <div className="m-client">{t.client_name}</div>
                  <div style={{ fontSize: 14, marginTop: 3 }}>{t.title}</div>
                  <div className="m-sub" style={{ marginTop: 5 }}>{formatHM(t.tracked_seconds)}</div>
                </button>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#10664d' }}>
                    {formatMoneyShort(Number(t.amount))}
                  </div>
                </div>
                <IconChevron color="#94a3b8" />
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ height: 24 }} />
    </>
  )
}
